import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ConfirmDialog } from '../components/ui/confirm-dialog'
import { SecretCreatePanel } from '../components/secrets/secret-create-panel'
import { SecretDetailPanel } from '../components/secrets/secret-detail-panel'
import { SecretImportExportPanel } from '../components/secrets/secret-import-export-panel'
import { SecretList } from '../components/secrets/secret-list'
import { SecretToolbar } from '../components/secrets/secret-toolbar'
import type {
  SecretConfirmAction,
  SecretDraft,
  SecretEntry,
  SecretGrant,
  SecretGrantDraft,
  SecretUpdateDraft,
  SecretVersion,
} from '../components/secrets/secret-dashboard-types'
import {
  actionSuccessText,
  confirmDescription,
  confirmTitle,
  isDangerAction,
  normalizeSecretPath,
} from '../components/secrets/secret-dashboard-utils'
import {
  useCreateSecret,
  useCreateSecretGrant,
  useDestroySecret,
  useRevokeSecretGrant,
  useRollbackSecret,
  useSecretAudit,
  useSecretGrants,
  useSecrets,
  useSecretVersions,
  useUpdateSecret,
} from '../hooks/use-secrets'
import { cn } from '../lib/utils'
import { useAppStore } from '../stores/app-store'

export function SecretsPage() {
  const { theme } = useAppStore()
  const isDark = theme === 'dark'
  const navigate = useNavigate()
  const [environment, setEnvironment] = useState('dev')
  const [path, setPath] = useState('/')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<SecretConfirmAction | null>(null)

  const filters = useMemo(() => ({ environment, path: normalizeSecretPath(path), search: search || undefined }), [environment, path, search])
  const secretsQuery = useSecrets(filters)
  const createSecret = useCreateSecret()
  const updateSecret = useUpdateSecret()
  const destroySecret = useDestroySecret()
  const grantsQuery = useSecretGrants()
  const createGrant = useCreateSecretGrant()
  const revokeGrant = useRevokeSecretGrant()
  const rollbackSecret = useRollbackSecret()

  const secrets = secretsQuery.data?.secrets ?? []
  const selected = secrets.find((item) => item.id === selectedId) ?? null
  const versionsQuery = useSecretVersions(selected?.id)
  const auditQuery = useSecretAudit({ environment: selected?.environment ?? environment, key: selected?.key, limit: 50 })

  async function handleCreate(draft: SecretDraft) {
    try {
      const created = await createSecret.mutateAsync({
        environment,
        path: normalizeSecretPath(path),
        key: draft.key.trim(),
        value: draft.value,
        kind: draft.kind,
        description: draft.description || null,
        reason: draft.reason,
      })
      setSelectedId(created.id)
      toast.success('Secret created')
    } catch (err) {
      toast.error('Create failed', { description: err instanceof Error ? err.message : undefined })
    }
  }

  async function handleUpdate(draft: SecretUpdateDraft) {
    if (!selected) return
    try {
      const updated = await updateSecret.mutateAsync({
        id: selected.id,
        value: draft.valueChanged ? draft.value : undefined,
        kind: draft.kind,
        description: draft.description || null,
        expectedVersion: selected.currentVersion,
        reason: draft.reason,
      })
      setSelectedId(updated.id)
      toast.success('Secret updated')
    } catch (err) {
      toast.error('Update failed', { description: err instanceof Error ? err.message : undefined })
    }
  }

  async function handleCreateGrant(draft: SecretGrantDraft & { expiresAt: number | null }) {
    try {
      await createGrant.mutateAsync({
        targetType: draft.targetType,
        targetId: draft.targetId,
        targetDisplayName: draft.targetDisplayName,
        environment: selected?.environment ?? environment,
        path: normalizeSecretPath(selected?.path ?? path),
        keyPattern: draft.keyPattern,
        permissions: draft.permissions,
        expiresAt: draft.expiresAt,
        reason: draft.reason,
      })
      toast.success('Grant created')
    } catch (err) {
      toast.error('Grant failed', { description: err instanceof Error ? err.message : undefined })
    }
  }

  async function runConfirmedAction() {
    if (!confirmAction) return
    try {
      if (confirmAction.type === 'disable') await destroySecret.mutateAsync({ id: confirmAction.entry.id })
      if (confirmAction.type === 'destroy') await destroySecret.mutateAsync({ id: confirmAction.entry.id, destroy: true })
      if (confirmAction.type === 'revoke-grant') await revokeGrant.mutateAsync(confirmAction.grant.id)
      if (confirmAction.type === 'rollback' && selected) {
        await rollbackSecret.mutateAsync({
          id: selected.id,
          version: confirmAction.version.version,
          expectedVersion: selected.currentVersion,
          reason: `rollback to v${confirmAction.version.version}`,
        })
      }
      if (confirmAction.type === 'disable' || confirmAction.type === 'destroy') setSelectedId(null)
      toast.success(actionSuccessText(confirmAction))
    } catch (err) {
      toast.error('Action failed', { description: err instanceof Error ? err.message : undefined })
    }
  }

  return (
    <div className={cn('flex h-dvh flex-col', isDark ? 'bg-surface-0 text-neutral-100' : 'bg-neutral-50 text-neutral-900')}>
      <SecretToolbar
        environment={environment}
        path={path}
        search={search}
        total={secretsQuery.data?.total ?? secrets.length}
        isDark={isDark}
        onBack={() => navigate(-1)}
        onEnvironmentChange={setEnvironment}
        onPathChange={setPath}
        onSearchChange={setSearch}
      />

      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[minmax(0,1fr)_440px]">
        <main className="space-y-4 overflow-auto p-4">
          <SecretCreatePanel environment={environment} path={normalizeSecretPath(path)} isDark={isDark} isPending={createSecret.isPending} onCreate={handleCreate} />
          <SecretList
            secrets={secrets}
            selectedId={selectedId}
            isLoading={secretsQuery.isLoading}
            error={secretsQuery.error as Error | null}
            isDark={isDark}
            onSelect={(entry: SecretEntry) => setSelectedId(entry.id)}
            onDisable={(entry: SecretEntry) => setConfirmAction({ type: 'disable', entry })}
          />
          <SecretImportExportPanel environment={environment} path={normalizeSecretPath(path)} isDark={isDark} />
        </main>

        <SecretDetailPanel
          entry={selected}
          versions={versionsQuery.data?.versions ?? []}
          grants={grantsQuery.data?.grants ?? []}
          auditEvents={auditQuery.data?.events ?? []}
          isDark={isDark}
          isUpdating={updateSecret.isPending}
          isCreatingGrant={createGrant.isPending}
          onUpdate={handleUpdate}
          onDisable={(entry: SecretEntry) => setConfirmAction({ type: 'disable', entry })}
          onDestroy={(entry: SecretEntry) => setConfirmAction({ type: 'destroy', entry })}
          onRollback={(version: SecretVersion) => setConfirmAction({ type: 'rollback', version })}
          onCreateGrant={handleCreateGrant}
          onRevokeGrant={(grant: SecretGrant) => setConfirmAction({ type: 'revoke-grant', grant })}
        />
      </div>

      <ConfirmDialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onConfirm={runConfirmedAction}
        title={confirmTitle(confirmAction)}
        description={confirmDescription(confirmAction)}
        confirmLabel={confirmAction?.type === 'destroy' ? 'Destroy' : 'Confirm'}
        variant={isDangerAction(confirmAction) ? 'danger' : 'info'}
      />
    </div>
  )
}
